<template>
  <div class="pa-rate">
    <!-- Option selector pills -->
    <div class="option-pills" role="tablist" aria-label="Coverage option">
      <button
        v-for="opt in options"
        :key="opt.code"
        type="button"
        role="tab"
        :aria-selected="opt.code === selectedOption"
        class="option-pill"
        :class="{ 'option-pill--active': opt.code === selectedOption }"
        @click="$emit('select-option', opt.code)"
      >
        <span class="pill-name">{{ opt.name }}</span>
        <span class="pill-premium">KES {{ formatPremium(premiumsForDuration[opt.code]) }}</span>
      </button>
    </div>

    <!-- Comparison table -->
    <div class="pa-rate-wrap">
      <table class="pa-rate-table">
        <thead>
          <tr>
            <th class="benefit-col">Benefit</th>
            <th
              v-for="opt in options"
              :key="opt.code"
              :class="['opt-col', { 'opt-col--active': opt.code === selectedOption }]"
            >
              {{ opt.name }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in benefitRows" :key="row.code">
            <td class="benefit-label">{{ row.label }}</td>
            <td
              v-for="opt in options"
              :key="opt.code + row.code"
              :class="['opt-val', { 'opt-val--active': opt.code === selectedOption }]"
            >
              <span class="limit-val">{{ formatLimit(row.limits[opt.code]) }}</span>
            </td>
          </tr>
          <tr class="premium-row">
            <td class="benefit-label premium-label">Premium · {{ durationLabel }}</td>
            <td
              v-for="opt in options"
              :key="'prem-' + opt.code"
              :class="['opt-val premium', { 'opt-val--active': opt.code === selectedOption }]"
            >
              <span class="premium-val">KES {{ formatPremium(premiumsForDuration[opt.code]) }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PaRateCardTable',
  props: {
    options: { type: Array, required: true },
    premiums: { type: Array, required: true },
    selectedOption: { type: String, required: true },
    courseDuration: { type: String, required: true },
  },
  computed: {
    benefitRows() {
      if (!this.options.length) return [];
      const codes = this.options[0].benefits.map((b) => b.code);
      return codes.map((code) => {
        const first = this.options[0].benefits.find((b) => b.code === code);
        const limits = {};
        this.options.forEach((opt) => {
          const b = opt.benefits.find((x) => x.code === code);
          limits[opt.code] = b?.limit ?? 0;
        });
        return { code, label: first?.label || code, limits };
      });
    },
    premiumsForDuration() {
      const row = this.premiums.find((p) => p.courseDuration === this.courseDuration);
      if (!row) return {};
      return {
        OPTION_1: row.OPTION_1,
        OPTION_2: row.OPTION_2,
        OPTION_3: row.OPTION_3,
      };
    },
    durationLabel() {
      const row = this.premiums.find((p) => p.courseDuration === this.courseDuration);
      return row?.label || this.courseDuration;
    },
  },
  methods: {
    formatLimit(n) {
      return Number(n || 0).toLocaleString('en-KE');
    },
    formatPremium(n) {
      return Number(n || 0).toLocaleString('en-KE');
    },
  },
};
</script>

<style scoped>
.pa-rate { display: flex; flex-direction: column; gap: 16px; }

/* Option pills */
.option-pills {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}
.option-pill {
  display: flex; flex-direction: column; align-items: flex-start; gap: 4px;
  padding: 14px 16px; border-radius: 12px;
  border: 2px solid #E2E8F0; background: #F8FAFC;
  cursor: pointer; transition: all 0.2s; text-align: left;
}
.option-pill:hover:not(.option-pill--active) {
  border-color: #94A3B8;
  background: #EFF6FF;
}
.option-pill--active {
  border-color: #184679;
  background: #184679;
  box-shadow: 0 4px 14px rgba(24, 70, 121, 0.22);
}
.option-pill--active:hover {
  background: #1a3d6b;
  border-color: #184679;
}
.pill-name { font-size: 0.9rem; font-weight: 700; color: #1E4276; }
.option-pill--active .pill-name { color: white; }
.pill-premium { font-size: 0.8rem; font-weight: 600; color: #64748B; }
.option-pill--active .pill-premium { color: rgba(255, 255, 255, 0.9); }

/* Table */
.pa-rate-wrap {
  overflow-x: auto;
  border-radius: 12px;
  border: 1px solid #E2E8F0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}
.pa-rate-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;
  min-width: 480px;
}
.pa-rate-table th,
.pa-rate-table td {
  padding: 12px 14px;
  border-bottom: 1px solid #E8EDF2;
}
.benefit-col,
.benefit-label {
  text-align: left !important;
  font-weight: 600;
  color: #475569;
  background: #F8FAFC;
  width: 38%;
}
.pa-rate-table thead th {
  background: #F1F5F9;
  color: #1E4276;
  font-weight: 700;
  font-size: 0.78rem;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.pa-rate-table thead th.opt-col--active {
  background: #184679;
  color: #ffffff;
}
.opt-val {
  text-align: center;
  color: #334155;
  background: white;
  transition: background 0.15s;
}
.opt-val--active {
  background: #EFF6FF;
  font-weight: 600;
  color: #1E4276;
}
.limit-val { font-variant-numeric: tabular-nums; }
.premium-row td { border-bottom: none; }
.premium-label { color: #1E4276; font-weight: 700; }
.premium-val {
  font-weight: 800;
  color: #184679;
  font-size: 0.88rem;
  font-variant-numeric: tabular-nums;
}
.opt-val--active .premium-val { color: #0369A1; }

@media (max-width: 640px) {
  .option-pills { grid-template-columns: 1fr; }
  .pa-rate-table {
    min-width: 0;
    font-size: 0.72rem;
  }
  .pa-rate-table th,
  .pa-rate-table td {
    padding: 10px 8px;
  }
  .benefit-col,
  .benefit-label {
    width: auto;
    min-width: 96px;
  }
  .option-pill {
    padding: 12px 14px;
  }
}

@media (max-width: 400px) {
  .pa-rate-wrap {
    margin-left: -4px;
    margin-right: -4px;
    border-radius: 10px;
  }
  .pa-rate-table {
    font-size: 0.68rem;
  }
  .pa-rate-table th,
  .pa-rate-table td {
    padding: 8px 6px;
  }
}
</style>
